/**
 * Aanwezigheidskalender — filterbalk (afdeling + aanwezig/afwezig).
 *
 * Mirrors the control idiom of `werkrooster/components/OrgScheduleFilters`
 * (select + segmented toggle + "Filters wissen") so the two overviews feel
 * like one product (AD-9: reuse the pattern family, not the code).
 *
 * The department select only renders when there is something to choose
 * between (>= 2 departments in the current response). That matters beyond
 * HR: `resolveTeam` returns every department a user manages, so a manager
 * of multiple departments sees several teams at once under "eigen team".
 */

import type { AanwezigheidFilterMode } from '../utils/telling';

interface AanwezigheidFiltersProps {
  /** `[id, label]` pairs, already sorted; may contain `GEEN_AFDELING` (see `lib/afdeling`). */
  departments: Array<[number, string]>;
  selectedDepartment: number | null;
  onDepartmentChange: (departmentId: number | null) => void;
  filterMode: AanwezigheidFilterMode;
  onFilterModeChange: (mode: AanwezigheidFilterMode) => void;
}

const MODES: Array<{ value: AanwezigheidFilterMode; label: string; activeClass: string }> = [
  { value: 'alle', label: 'Alle', activeClass: 'bg-white shadow-sm text-gray-900' },
  { value: 'aanwezig', label: 'Aanwezig', activeClass: 'bg-green-100 text-green-700 shadow-sm' },
  { value: 'afwezig', label: 'Afwezig', activeClass: 'bg-[#2F5A7A]/10 text-[#2F5A7A] shadow-sm' },
];

export function AanwezigheidFilters({
  departments,
  selectedDepartment,
  onDepartmentChange,
  filterMode,
  onFilterModeChange,
}: AanwezigheidFiltersProps) {
  const showDepartmentFilter = departments.length >= 2;
  // Bewust NIET afhankelijk van `showDepartmentFilter`: als de afdelingslijst
  // krimpt terwijl er een afdeling gekozen is, moet de wis-actie juist wél
  // blijven staan — anders zit je vast in een filter dat je niet meer ziet.
  const hasActiveFilters = selectedDepartment !== null || filterMode !== 'alle';

  const clearFilters = () => {
    onDepartmentChange(null);
    onFilterModeChange('alle');
  };

  return (
    <div data-testid="aanwezigheid-filters" className="flex items-center gap-3 flex-wrap">
      {showDepartmentFilter && (
        <div className="flex items-center gap-2">
          <label htmlFor="aanwezigheid-afdeling" className="text-sm text-gray-500">
            Team:
          </label>
          <select
            id="aanwezigheid-afdeling"
            data-testid="aanwezigheid-afdeling-filter"
            value={selectedDepartment ?? ''}
            onChange={(e) => onDepartmentChange(e.target.value ? parseInt(e.target.value, 10) : null)}
            className="px-3 py-1.5 text-sm border border-[#E2E8F0] rounded-lg text-[#1E293B] focus:ring-2 focus:ring-[#2F5A7A] focus:border-[#2F5A7A]"
          >
            <option value="">Alle teams</option>
            {departments.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div
        role="group"
        aria-label="Filter op aanwezigheid"
        className="inline-flex rounded-lg border border-[#E2E8F0] p-0.5 bg-gray-50"
      >
        {MODES.map((mode) => (
          <button
            key={mode.value}
            type="button"
            data-testid={`aanwezigheid-mode-${mode.value}`}
            aria-pressed={filterMode === mode.value}
            onClick={() => onFilterModeChange(mode.value)}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              filterMode === mode.value ? mode.activeClass : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {hasActiveFilters && (
        <button
          type="button"
          data-testid="aanwezigheid-filters-wissen"
          onClick={clearFilters}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
        >
          Filters wissen
        </button>
      )}
    </div>
  );
}
